import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  Tag, 
  Modal,
  message,
  Badge,
  Tabs,
  DatePicker,
  Row,
  Col,
  Form,
  Descriptions,
  Checkbox
} from 'antd';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { renderBillTypeTag } from '../../../utils/billTypeConfig';
import { 
  SearchOutlined, 
  ReloadOutlined,
  CalendarOutlined,
  HistoryOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { getBillTypeLabel } from '../../../utils/billTypeConfig';
import dayjs from 'dayjs';
import { GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, type AllSettlementClientResponse } from './graphql/queries/getAllSettlementClient';
import { settlementClientRoleTypeOptions, SettlementClientRoleType, type SettlementClientRoleTypeValue } from './config/settlementClientRoleType';
import { BillRerunProgressModal } from '../../../components/settlement/BillRerunProgressModal';
import { BillTimeRangePicker } from '../../../components/settlement/BillTimeRangePicker';
import { formatTimeWithTimezone } from '../../../utils/timezoneFormatter';
import { useQuery, useMutation } from '@apollo/client';
import { GET_RERUNNABLE_PERIOD_INSTANCES } from '../../../graphql/queries/settlement/getSettlementBillQueries';
import { GET_BILL_RERUN_HISTORY } from '../../../graphql/queries/settlement/billRerunQueries';
import { RERUN_BILL, RERUN_BATCH_BILLS, DELETE_BILLING_PERIOD_INSTANCE } from '../../../graphql/mutations/settlement/settlementBillMutations';
import { BillDetailModal } from '../../../components/settlement/BillDetailModal';
import { BillRerunProgress } from '../../../graphql/types/settlement/settlementBillTypes';
import type { 
  BillingPeriodInstanceType,
  PageRequestTypeInput
} from '../../../graphql/types/settlement';
import type { BillTimeRangeInput } from '../../../graphql/types/settlement/settlementBillTypes';
import { useTranslation } from 'react-i18next';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

/**
 * 账单重跑工具页面
 * 支持两种重跑模式：账期实例重跑和历史时间重跑
 */
export const SettlementBillRerun: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const { t } = useTranslation();
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>();
  const [clientRoleType, setClientRoleType] = useState<SettlementClientRoleTypeValue>(SettlementClientRoleType.CHANNEL_CLIENT);
  const [pageRequest, setPageRequest] = useState<PageRequestTypeInput>({ page: 1, size: 20 });
  const [selectedInstances, setSelectedInstances] = useState<BillingPeriodInstanceType[]>([]);
  const [rerunModalVisible, setRerunModalVisible] = useState(false);
  const [rerunReason, setRerunReason] = useState('');
  
  // 重跑进度弹窗状态
  const [rerunProgressVisible, setRerunProgressVisible] = useState(false);
  const [rerunTaskId, setRerunTaskId] = useState<string | null>(null);
  
  // 历史重跑相关状态
  const [historicalRerunForm] = Form.useForm();
  const [historicalRerunModalVisible, setHistoricalRerunModalVisible] = useState(false);
  const [historicalRerunLoading, setHistoricalRerunLoading] = useState(false);
  
  // 账单详情弹窗相关状态
  const [billModalVisible, setBillModalVisible] = useState(false);
  const [selectedPeriodInstanceId, setSelectedPeriodInstanceId] = useState<string | null>(null);
  
  // 是否显示已作废账期实例
  const [includeCancelled, setIncludeCancelled] = useState(false);

  // GraphQL 查询
  const { data, loading, refetch } = useQuery(GET_RERUNNABLE_PERIOD_INSTANCES, {
    variables: { 
      clientId: selectedClientId && selectedClientId.trim() !== '' ? selectedClientId : undefined,
      pageRequest,
      includeCancelled
    },
    fetchPolicy: 'cache-and-network',
    skip: !selectedClientId || selectedClientId.trim() === '' // 没有选择客户时跳过查询
  });

  // 重跑历史查询（使用新的任务历史接口）
  const { data: historyData, loading: historyLoading, refetch: refetchHistory } = useQuery<{
    getBillRerunHistory: BillRerunProgress[];
  }>(GET_BILL_RERUN_HISTORY, {
    variables: { 
      clientId: selectedClientId
    },
    skip: !selectedClientId || selectedClientId.trim() === '',
    fetchPolicy: 'cache-and-network'
  });

  // 重跑账单变更
  const [rerunBill] = useMutation(RERUN_BILL);
  const [rerunBatchBills] = useMutation(RERUN_BATCH_BILLS);
  
  // 删除账期实例变更
  const [deletePeriodInstance] = useMutation(DELETE_BILLING_PERIOD_INSTANCE);

  const { data: settlementClientData, loading: settlementClientLoading } = useQuery<AllSettlementClientResponse>(
    GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS,
    {
      variables: { roleType: clientRoleType },
      fetchPolicy: 'cache-and-network'
    }
  );

  const settlementClientOptions = settlementClientData?.allOptions || [];
  const clientLabel = clientRoleType === SettlementClientRoleType.DSP_CLIENT ? 'DSP' : '客户(channel)';

  // 处理客户筛选
  const handleClientFilter = (value: string) => {
    setSelectedClientId(value || undefined);
  };

  const handleClientRoleTypeChange = (value: SettlementClientRoleTypeValue) => {
    setClientRoleType(value);
    setSelectedClientId(undefined);
    setSelectedInstances([]);
    historicalRerunForm.setFieldsValue({ clientId: undefined });
  };

  // 处理分页变化
  const handlePageChange = (page: number, size: number) => {
    setPageRequest({ page, size });
  };

  // 处理选择实例
  const handleSelectInstances = (selectedRowKeys: React.Key[], selectedRows: BillingPeriodInstanceType[]) => {
    setSelectedInstances(selectedRows);
  };

  // 处理单个重跑
  const handleSingleRerun = (instance: BillingPeriodInstanceType) => {
    setSelectedInstances([instance]);
    setRerunModalVisible(true);
  };

  // 处理批量重跑
  const handleBatchRerun = () => {
    if (selectedInstances.length === 0) {
      message.warning('请选择要重跑的账期实例');
      return;
    }
    setRerunModalVisible(true);
  };

  // 处理历史重跑
  const handleHistoricalRerun = () => {
    setHistoricalRerunModalVisible(true);
  };

  // 处理点击账期实例查看账单
  const handleViewBill = (periodInstanceId: string) => {
    setSelectedPeriodInstanceId(periodInstanceId);
    setBillModalVisible(true);
  };

  // 关闭账单弹窗
  const handleCloseBillModal = () => {
    setBillModalVisible(false);
    setSelectedPeriodInstanceId(null);
  };

  // 执行历史重跑
  const handleExecuteHistoricalRerun = async () => {
    try {
      const values = await historicalRerunForm.validateFields();
      setHistoricalRerunLoading(true);
      
      const timeRange = values.periodRange as BillTimeRangeInput;
      
      // TODO: 实现历史时间重跑的后端接口
      // 需要传递时区信息给后端，后端将时间转换为 UTC 进行查询
      console.log('历史重跑参数:', {
        clientId: values.clientId,
        billType: values.billType,
        startTime: timeRange.startTime,
        endTime: timeRange.endTime,
        timeZone: timeRange.timeZoneId,
        reason: values.reason
      });
      
      message.info('历史重跑功能正在开发中，已接收参数（含时区）');
      setHistoricalRerunModalVisible(false);
      historicalRerunForm.resetFields();
    } catch (error) {
      message.error('历史重跑失败: ' + (error as Error).message);
    } finally {
      setHistoricalRerunLoading(false);
    }
  };

  // 格式化账期时间显示（已移除，统一使用 formatTimeWithTimezone）

  // 处理删除账期实例
  const handleDeleteInstance = async (instance: BillingPeriodInstanceType) => {
    try {
      Modal.confirm({
        title: '确认删除',
        content: (
          <div>
            <p>确定要删除账期实例 <strong>{instance.id}</strong> 吗？</p>
            
            <div className="mt-3 p-3 bg-gray-50 rounded-md">
              <p className="text-sm font-medium text-gray-700 mb-2">账期信息：</p>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>账期实例ID：</strong>{instance.id}</p>
                <p><strong>客户：</strong>{instance.clientName}</p>
                <p><strong>账单类型：</strong>{getBillTypeLabel(instance.billType)}</p>
                <p><strong>账期时间：</strong>
                  {instance.periodStart && instance.periodEnd && instance.timeZone 
                    ? `${formatTimeWithTimezone(instance.periodStart, instance.timeZone)} ~ ${formatTimeWithTimezone(instance.periodEnd, instance.timeZone)}`
                    : `${instance.periodStart} ~ ${instance.periodEnd}`
                  }
                </p>
                <p><strong>时区：</strong>{instance.timeZone}</p>
                <p><strong>状态：</strong>{
                  (() => {
                    const statusConfig = {
                      'INACTIVE': '未激活',
                      'ACTIVE': '激活',
                      'CLOSED': '已关账',
                      'CANCELLED': '已作废',
                    };
                    return statusConfig[instance.status as keyof typeof statusConfig] || instance.status;
                  })()
                }</p>
              </div>
            </div>
            
            <p className="mt-3 text-sm text-gray-600">
              <strong>删除后：</strong>
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
              <li>账期实例将被标记为已作废</li>
              <li>对应的账单（如果存在）也将被作废</li>
              <li>此操作不可恢复</li>
            </ul>
            {instance.status === 'ACTIVE' && (
              <p className="mt-2 text-sm text-orange-600">
                ⚠️ 注意：该账期实例当前为激活状态，删除请谨慎
              </p>
            )}
            {instance.status === 'CANCELLED' && (
              <p className="mt-2 text-sm text-gray-600">
                ℹ️ 该账期实例已经作废，不需要删除
              </p>
            )}
            {instance.isIssued === 1 && (
              <p className="mt-2 text-sm text-red-600">
                ❌ 该账期实例已出账，无法删除
              </p>
            )}
          </div>
        ),
        okText: '确认删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          try {
            // 检查是否已出账
            if (instance.isIssued === 1) {
              message.error('已出账的账期实例无法删除');
              return;
            }
            
            const result = await deletePeriodInstance({
              variables: {
                instanceId: instance.id
              }
            });
            
            if (result.data?.deleteBillingPeriodInstance?.success) {
              message.success('账期实例删除成功');
              refetch();
            } else {
              message.error(result.data?.deleteBillingPeriodInstance?.message || '删除失败');
            }
          } catch (error) {
            message.error('删除失败: ' + (error as Error).message);
          }
        }
      });
    } catch (error) {
      message.error('删除失败: ' + (error as Error).message);
    }
  };

  // 执行重跑
  const handleExecuteRerun = async () => {
    if (selectedInstances.length === 0) {
      message.warning('请选择要重跑的账期实例');
      return;
    }

    if (!rerunReason.trim()) {
      message.error('请输入重跑原因');
      return;
    }

    try {
      if (selectedInstances.length === 1) {
        // 单个重跑（异步）
        const result = await rerunBill({
          variables: {
            rerunRequest: {
              clientId: selectedInstances[0].clientId,
              periodInstanceId: selectedInstances[0].id,
              reason: rerunReason
            }
          }
        });

        if (result.data?.rerunBill?.success) {
          const taskId = result.data.rerunBill.taskId;
          message.success('重跑任务已创建');
          setRerunModalVisible(false);
          setRerunReason('');
          
          // 显示进度弹窗
          setRerunTaskId(taskId);
          setRerunProgressVisible(true);
        } else {
          message.error(result.data?.rerunBill?.message || '重跑失败');
        }
      } else {
        // 批量重跑
        const rerunRequests = selectedInstances.map(instance => ({
          clientId: instance.clientId,
          periodInstanceId: instance.id,
          reason: rerunReason
        }));

        const result = await rerunBatchBills({
          variables: {
            batchRequest: { rerunRequests }
          }
        });

        if (result.data?.rerunBatchBills) {
          const { successCount, failureCount } = result.data.rerunBatchBills;
          message.success(`批量重跑完成: 成功 ${successCount} 个，失败 ${failureCount} 个`);
          setRerunModalVisible(false);
          setRerunReason('');
          setSelectedInstances([]);
          refetch();
          refetchHistory();
        }
      }
    } catch (error) {
      message.error('重跑失败: ' + (error as Error).message);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '账期实例ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <Button 
          type="link" 
          onClick={() => handleViewBill(id)}
          className="p-0 h-auto"
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: '客户名称',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150,
    },
    {
      title: '账单类型',
      dataIndex: 'billType',
      key: 'billType',
      width: 120,
      render: (billType: string) => {
        const billTypeConfig = {
          'DELIVERY_FEE_BILL': { color: 'blue', text: '运费账单' },
          'SLA_BILL': { color: 'green', text: 'SLA账单' },
          'DSP_DELIVERY_FEE_BILL': { color: 'purple', text: 'DSP运费账单' },
          'SORTING_BILL': { color: 'orange', text: '分拣账单' },
        };
        const config = billTypeConfig[billType as keyof typeof billTypeConfig];
        return <Tag color={config?.color}>{config?.text || billType}</Tag>;
      },
    },
    {
      title: '账期开始时间',
      dataIndex: 'periodStart',
      key: 'periodStart',
      width: 180,
      render: (time: string, record: BillingPeriodInstanceType) => 
        time && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : '-',
    },
    {
      title: '账期结束时间',
      dataIndex: 'periodEnd',
      key: 'periodEnd',
      width: 180,
      render: (time: string, record: BillingPeriodInstanceType) => 
        time && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : '-',
    },
    {
      title: '时区',
      dataIndex: 'timeZone',
      key: 'timeZone',
      width: 150,
    },
    {
      title: '账期状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = {
          'INACTIVE': { color: 'default', text: '未激活' },
          'ACTIVE': { color: 'green', text: '激活' },
          'CLOSED': { color: 'blue', text: '已关账' },
          'CANCELLED': { color: 'red', text: '已作废' },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return config ? <Tag color={config.color}>{config.text}</Tag> : <Tag>{status}</Tag>;
      },
    },
    {
      title: '是否可重跑',
      dataIndex: 'canRerun',
      key: 'canRerun',
      width: 100,
      render: (canRerun: boolean) => (
        <Badge 
          status={canRerun ? 'success' : 'error'} 
          text={canRerun ? '可重跑' : '不可重跑'} 
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (record: BillingPeriodInstanceType) => (
        <div className="flex gap-2">
          {canOperate && (
            <Button
              type="link"
              icon={<ReloadOutlined />}
              disabled={!record.canRerun}
              onClick={() => handleSingleRerun(record)}
              className="p-0 h-auto"
            >
              重跑
            </Button>
          )}
          {canOperate && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={record.isIssued === 1 || record.status === 'CANCELLED'}
              onClick={() => handleDeleteInstance(record)}
              className="p-0 h-auto"
              title={
                record.isIssued === 1
                  ? '已出账的账期实例不能删除'
                  : record.status === 'CANCELLED'
                  ? '已作废的账期实例不需要删除'
                  : '删除账期实例'
              }
            >
              删除
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card title="账单重跑工具" className="mb-4">
        <Tabs defaultActiveKey="period-instance" type="card">
            {/* 账期实例重跑标签页 */}
            <TabPane 
              tab={
                <span>
                  <CalendarOutlined />
                  账期实例重跑
                </span>
              } 
              key="period-instance"
            >
              <div className="space-y-6">
                {/* 筛选区域 */}
                <Card size="small" className="bg-gray-50">
                  <Row gutter={[24, 16]} align="middle">
                    <Col span={6}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">客户类型</label>
                        <Select
                          value={clientRoleType}
                          onChange={handleClientRoleTypeChange}
                          options={settlementClientRoleTypeOptions}
                          className="w-full"
                        />
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{clientLabel}筛选</label>
                        <Select
                          value={selectedClientId}
                          onChange={(clientId) => {
                            const normalizedClientId = clientId || undefined;
                            setSelectedClientId(normalizedClientId);
                            handleClientFilter(normalizedClientId || '');
                          }}
                          placeholder={`请选择${clientLabel}`}
                          options={settlementClientOptions}
                          showSearch
                          optionFilterProp="label"
                          loading={settlementClientLoading}
                          allowClear
                          className="w-full"
                        />
                      </div>
                    </Col>
                    <Col span={6}>
                      <div className="flex items-center h-8">
                        <Checkbox
                          checked={includeCancelled}
                          onChange={(e) => {
                            setIncludeCancelled(e.target.checked);
                            setPageRequest(prev => ({ ...prev, page: 1 }));
                          }}
                          className="text-sm"
                        >
                          显示已作废账期
                        </Checkbox>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div className="flex items-center justify-end space-x-2">
                        {canOperate && (
                          <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            disabled={selectedInstances.length === 0}
                            onClick={handleBatchRerun}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            批量重跑 ({selectedInstances.length})
                          </Button>
                        )}
                        {canOperate && (
                          <Button
                            type="default"
                            icon={<SearchOutlined />}
                            onClick={() => {
                              if (!selectedClientId || selectedClientId.trim() === '') {
                                message.warning('请先选择客户');
                                return;
                              }
                              refetch();
                            }}
                            disabled={!selectedClientId || selectedClientId.trim() === ''}
                            title={!selectedClientId || selectedClientId.trim() === '' ? '请先选择客户' : '查询可重跑账期实例'}
                            className="border-gray-300 hover:border-blue-500"
                          >
                            查询
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>

                {/* 可重跑账期实例表格 */}
                <Card size="small">
                  <Table
                    columns={columns}
                    dataSource={data?.getRerunnablePeriodInstances?.content || []}
                    loading={loading}
                    rowKey="id"
                    pagination={false}
                    rowSelection={{
                      selectedRowKeys: selectedInstances.map(instance => instance.id),
                      onChange: handleSelectInstances,
                      getCheckboxProps: (record: BillingPeriodInstanceType) => ({
                        disabled: !record.canRerun,
                      }),
                    }}
                    scroll={{ x: 1000 }}
                    size="small"
                    className="rounded-lg"
                    locale={{
                      emptyText: !selectedClientId || selectedClientId.trim() === '' 
                        ? '请先选择客户进行查询' 
                        : '暂无可重跑的账期实例'
                    }}
                  />
                </Card>

                {data?.getRerunnablePeriodInstances?.pageInfo && (
                  <TablePaginationBar
                    total={data.getRerunnablePeriodInstances.pageInfo.totalElements}
                    current={data.getRerunnablePeriodInstances.pageInfo.page}
                    pageSize={data.getRerunnablePeriodInstances.pageInfo.size}
                    onChange={handlePageChange}
                    paginationProps={{ className: 'pagination-custom' }}
                  />
                )}
              </div>
            </TabPane>

            {/* 重跑历史标签页 */}
            <TabPane
              tab={
                <span>
                  <HistoryOutlined />
                  重跑历史
                </span>
              }
              key="rerun-history"
            >
              <div className="space-y-6">
                {/* 筛选区域 */}
                <Card size="small" className="bg-gray-50">
                  <Row gutter={[24, 16]} align="middle">
                    <Col span={8}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">客户类型</label>
                        <Select
                          value={clientRoleType}
                          onChange={handleClientRoleTypeChange}
                          options={settlementClientRoleTypeOptions}
                          className="w-full"
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{clientLabel}筛选</label>
                        <Select
                          value={selectedClientId}
                          onChange={(clientId) => {
                            setSelectedClientId(clientId || undefined);
                          }}
                          placeholder={`请选择${clientLabel}`}
                          options={settlementClientOptions}
                          showSearch
                          optionFilterProp="label"
                          loading={settlementClientLoading}
                          allowClear
                          className="w-full"
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div className="flex items-center justify-end">
                        {canOperate && (
                          <Button
                            type="default"
                            icon={<SearchOutlined />}
                            onClick={() => {
                              if (!selectedClientId || selectedClientId.trim() === '') {
                                message.warning('请先选择客户');
                                return;
                              }
                              refetchHistory();
                            }}
                            disabled={!selectedClientId || selectedClientId.trim() === ''}
                            title={!selectedClientId || selectedClientId.trim() === '' ? '请先选择客户' : '查询重跑历史'}
                            className="border-gray-300 hover:border-blue-500"
                          >
                            查询
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>

                {/* 重跑历史表格 */}
                <Card size="small">
                  <Table
                  columns={[
                    {
                      title: '任务ID',
                      dataIndex: 'taskId',
                      key: 'taskId',
                      width: 80,
                      fixed: 'left',
                      render: (id: string) => (
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{id}</span>
                      ),
                    },
                    {
                      title: '客户名称',
                      dataIndex: 'clientName',
                      key: 'clientName',
                      width: 120,
                      render: (name: string) => name || '-',
                    },
                    {
                      title: '账单类型',
                      dataIndex: 'billType',
                      key: 'billType',
                      width: 120,
                      render: (billType: string) => {
                        const billTypeConfig = {
                          'DELIVERY_FEE_BILL': { color: 'blue', text: '运费账单' },
                          'SLA_BILL': { color: 'green', text: 'SLA账单' },
                          'DSP_DELIVERY_FEE_BILL': { color: 'purple', text: 'DSP运费账单' },
                          'SORTING_BILL': { color: 'orange', text: '分拣账单' },
                        };
                        const config = billTypeConfig[billType as keyof typeof billTypeConfig];
                        return config ? <Tag color={config.color}>{config.text}</Tag> : <Tag>{billType}</Tag>;
                      },
                    },
                    {
                      title: '账期实例ID',
                      dataIndex: 'periodInstanceId',
                      key: 'periodInstanceId',
                      width: 100,
                      render: (id: string) => (
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{id}</span>
                      ),
                    },
                    {
                      title: '账期开始时间',
                      dataIndex: 'periodStart',
                      key: 'periodStart',
                      width: 180,
                      render: (time: string, record: BillRerunProgress) => 
                        time && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : '-',
                    },
                    {
                      title: '账期结束时间',
                      dataIndex: 'periodEnd',
                      key: 'periodEnd',
                      width: 180,
                      render: (time: string, record: BillRerunProgress) => 
                        time && record.timeZone ? formatTimeWithTimezone(time, record.timeZone) : '-',
                    },
                    {
                      title: '时区',
                      dataIndex: 'timeZone',
                      key: 'timeZone',
                      width: 150,
                    },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      key: 'status',
                      width: 90,
                      render: (status: number) => {
                        const statusConfig: Record<number, { text: string; color: string }> = {
                          0: { text: '待处理', color: 'default' },
                          1: { text: '处理中', color: 'processing' },
                          2: { text: '成功', color: 'success' },
                          3: { text: '失败', color: 'error' }
                        };
                        const config = statusConfig[status] || { text: '未知', color: 'default' };
                        return <Tag color={config.color}>{config.text}</Tag>;
                      },
                    },
                    {
                      title: '进度',
                      dataIndex: 'progress',
                      key: 'progress',
                      width: 80,
                      render: (progress: number) => `${progress}%`,
                    },
                    {
                      title: '原账单ID',
                      dataIndex: 'originalBillId',
                      key: 'originalBillId',
                      width: 100,
                      render: (id: string) => id ? (
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{id}</span>
                      ) : '-',
                    },
                    {
                      title: '新账单ID',
                      dataIndex: 'newBillId',
                      key: 'newBillId',
                      width: 100,
                      render: (id: string) => id ? (
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{id}</span>
                      ) : '-',
                    },
                    {
                      title: '重跑原因',
                      dataIndex: 'reason',
                      key: 'reason',
                      width: 200,
                      ellipsis: true,
                      render: (reason: string) => reason || '-',
                    },
                    {
                      title: '创建时间 (本地时区)',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      width: 180,
                      render: (time: string) => time ? new Date(time).toLocaleString('zh-CN') : '-',
                    },
                    {
                      title: '完成时间 (本地时区)',
                      dataIndex: 'completedAt',
                      key: 'completedAt',
                      width: 180,
                      render: (time: string) => time ? new Date(time).toLocaleString('zh-CN') : '-',
                    },
                    {
                      title: '错误信息',
                      dataIndex: 'errorMessage',
                      key: 'errorMessage',
                      width: 200,
                      ellipsis: true,
                      render: (msg: string) => msg ? (
                        <span style={{ color: '#ff4d4f' }}>{msg}</span>
                      ) : '-',
                    },
                  ]}
                  dataSource={historyData?.getBillRerunHistory || []}
                  loading={historyLoading}
                  rowKey="taskId"
                  pagination={false}
                  scroll={{ x: 1800 }}
                  size="small"
                  className="rounded-lg"
                  locale={{
                    emptyText: !selectedClientId || selectedClientId.trim() === '' 
                      ? '请先选择客户进行查询' 
                      : '暂无重跑历史记录'
                  }}
                />
                </Card>
              </div>
            </TabPane>
          </Tabs>
        </Card>

        {/* 账期实例重跑确认模态框 */}
        <Modal
          title="确认重跑"
          open={rerunModalVisible}
          onCancel={() => setRerunModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setRerunModalVisible(false)}>
              取消
            </Button>,
            <Button
              key="confirm"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleExecuteRerun}
            >
              确认重跑
            </Button>
          ]}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">重跑原因</label>
              <Input.TextArea
                placeholder="请输入重跑原因"
                value={rerunReason}
                onChange={(e) => setRerunReason(e.target.value)}
                rows={3}
              />
            </div>
            
            <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-800">
              <div className="font-medium mb-1">重跑说明:</div>
              <ul className="list-disc list-inside space-y-1">
                <li>重跑将重新生成指定账期的账单</li>
                <li><strong>系统会自动执行账单汇总操作</strong></li>
                <li>重跑过程中请勿重复操作</li>
                <li>重跑完成后会更新账单状态</li>
              </ul>
            </div>
          </div>
        </Modal>

        {/* 历史时间重跑模态框 */}
        <Modal
          title="历史时间重跑"
          open={historicalRerunModalVisible}
          onCancel={() => setHistoricalRerunModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setHistoricalRerunModalVisible(false)}>
              取消
            </Button>,
            <Button
              key="confirm"
              type="primary"
              icon={<HistoryOutlined />}
              loading={historicalRerunLoading}
              onClick={handleExecuteHistoricalRerun}
            >
              确认重跑
            </Button>
          ]}
          width={600}
        >
          <Form form={historicalRerunForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="客户类型">
                  <Select
                    value={clientRoleType}
                    onChange={handleClientRoleTypeChange}
                    options={settlementClientRoleTypeOptions}
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={clientLabel}
                  name="clientId"
                  rules={[{ required: true, message: `请选择${clientLabel}` }]}
                >
                  <Select
                    placeholder={`请选择${clientLabel}`}
                    options={settlementClientOptions}
                    showSearch
                    optionFilterProp="label"
                    loading={settlementClientLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="账单类型"
                  name="billType"
                  rules={[{ required: true, message: '请选择账单类型' }]}
                  initialValue="DELIVERY_FEE_BILL"
                >
                  <Select placeholder="选择账单类型">
                    <Option value="DELIVERY_FEE_BILL">运费账单</Option>
                    <Option value="SLA_BILL">SLA 账单</Option>
                    <Option value="DSP_DELIVERY_FEE_BILL">DSP运费账单</Option>
                    <Option value="SORTING_BILL">分拣账单</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item
              label="时间范围"
              name="periodRange"
              rules={[{ required: true, message: '请选择时间范围' }]}
            >
              <BillTimeRangePicker />
            </Form.Item>
            
            <Form.Item
              label="重跑原因"
              name="reason"
              rules={[{ required: true, message: '请输入重跑原因' }]}
            >
              <Input.TextArea
                placeholder="请输入重跑原因"
                rows={3}
              />
            </Form.Item>
          </Form>
          
          <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
            <div className="font-medium mb-1">历史重跑说明:</div>
            <ul className="list-disc list-inside space-y-1">
              <li>历史重跑将重新计算指定时间范围内的账单</li>
              <li>会自动作废该时间段内的原账单</li>
              <li><strong>系统会自动执行账单汇总和出账操作</strong></li>
              <li>适用于历史数据修正、规则变更等场景</li>
            </ul>
          </div>
        </Modal>

        {/* 账单详情弹窗 */}
        <BillDetailModal
          visible={billModalVisible}
          periodInstanceId={selectedPeriodInstanceId}
          onClose={handleCloseBillModal}
        />

        {/* 重跑进度弹窗 */}
        <BillRerunProgressModal
          visible={rerunProgressVisible}
          taskId={rerunTaskId}
          onClose={() => setRerunProgressVisible(false)}
          onCompleted={(success) => {
            if (success) {
              refetch(); // 刷新账单列表
              refetchHistory(); // 刷新重跑历史
              // 通知其他页面刷新数据
              window.dispatchEvent(new CustomEvent('billDataUpdated'));
            }
          }}
        />
    </div>
  );
};

export default SettlementBillRerun;

